import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/PageHeader";
import {
  Leaf, Plus, Droplets, Sun, Thermometer, RefreshCw, Trash2, AlertTriangle, Sparkles, Camera,
} from "lucide-react";
import ImageUploader, { imageUrl } from "@/components/ImageUploader";
import { toast } from "sonner";

// Deterministic varied handwritten captions for where a plant "lives".
// Chosen from plant.id so each plant keeps its unique voice across renders.
const LOCATION_PHRASES = [
  (loc) => `living in the ${loc}`,
  (loc) => `breathing in the ${loc}`,
  (loc) => `sunbathing in the ${loc}`,
  (loc) => `quietly thriving in the ${loc}`,
  (loc) => `keeping watch over the ${loc}`,
  (loc) => `settled beside the ${loc}`,
  (loc) => `dreaming in the ${loc}`,
  (loc) => `stretching towards the ${loc} light`,
  (loc) => `rooted in the ${loc}`,
  (loc) => `growing gently in the ${loc}`,
  (loc) => `keeping company in the ${loc}`,
  (loc) => `unfurling in the ${loc}`,
  (loc) => `soaking up the ${loc} mornings`,
  (loc) => `sighing in the ${loc}`,
  (loc) => `at home in the ${loc}`,
];

function locationPhrase(plantId, location) {
  // Simple stable hash so the same plant always shows the same caption.
  const key = String(plantId || location || "");
  let h = 0;
  for (let i = 0; i < key.length; i++) {
    h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % LOCATION_PHRASES.length;
  return LOCATION_PHRASES[idx](location);
}

export default function Plants() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { name: "", location: "", nickname: "" };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/plants"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  // If any existing plant has a cached AI error (e.g. budget was 0 at creation),
  // silently re-attempt once now that the AI is likely available again.
  const [autoRetried, setAutoRetried] = useState(new Set());
  useEffect(() => {
    const broken = items.filter((p) => p.care?.error && !autoRetried.has(p.id));
    if (broken.length === 0) return;
    broken.forEach((p) => {
      setAutoRetried((s) => new Set(s).add(p.id));
      api.post(`/plants/${p.id}/refresh`).then(() => load()).catch(() => {});
    });
  }, [items, autoRetried, load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Plant name required");
    setBusy(true);
    try {
      await api.post("/plants", form);
      setOpen(false); setForm(blank);
      toast.success("Plant added & care guide fetched");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const refresh = async (id) => {
    setRefreshing(id);
    try { await api.post(`/plants/${id}/refresh`); toast.success("Care guide refreshed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefreshing(null); }
  };
  const remove = async (id) => { await api.delete(`/plants/${id}`); load(); };

  return (
    <div className="green-room">
      <header className="mb-8" data-testid="green-room-header">
        <p className="green-room__eyebrow" data-testid="green-room-eyebrow">The Green Room</p>
        <h1 className="green-room__title text-4xl md:text-5xl lg:text-6xl mt-2">
          A quiet corner for growing things.
        </h1>
        <p className="green-room__lede mt-3 max-w-2xl text-lg md:text-xl">
          Add a plant and our resident gardener will draft a considered guide —
          watering, light, temperament — from the first leaf onwards.
        </p>
        <div className="mt-6 flex justify-start">
          <button
            data-testid="plant-add-button"
            onClick={() => setOpen(true)}
            className="btn-botanica inline-flex items-center text-sm"
          >
            <Plus size={15} className="mr-1.5" /> Add a plant
          </button>
        </div>
      </header>

      {items.length === 0 ? (
        <EmptyState icon={Leaf} title="No plants yet." message="Add the first one and we'll pull together watering, light, soil and seasonal tips." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => {
            const c = p.care || {};
            const hasError = c.error;
            return (
              <div key={p.id} data-testid={`plant-card-${p.id}`}
                   className="botanica-card overflow-hidden fade-up cursor-pointer flex flex-col"
                   onClick={() => setSelected(p)}>
                <div className="h-40 bg-hearth-stone relative overflow-hidden">
                  {p.image_path ? (
                    <img src={imageUrl(p.image_path)} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <img
                        src={`https://source.unsplash.com/400x300/?${encodeURIComponent(p.name)},plant,houseplant`}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "grid"; }}
                        data-testid={`plant-auto-photo-${p.id}`}
                      />
                      <div className="w-full h-full grid-cols-1 place-items-center absolute inset-0 hidden" style={{ display: "none" }}>
                        <Leaf size={36} style={{ color: "rgba(30,58,46,0.35)" }} />
                      </div>
                    </>
                  )}
                  <button data-testid={`plant-refresh-${p.id}`} onClick={(e) => { e.stopPropagation(); refresh(p.id); }}
                          className="absolute top-3 right-3 p-2 rounded-lg bg-hearth-paper/90 text-hearth-mute hover:text-hearth-clay backdrop-blur">
                    <RefreshCw size={13} className={refreshing === p.id ? "animate-spin" : ""} />
                  </button>
                  <label onClick={(e) => e.stopPropagation()} className="absolute bottom-3 right-3 p-2 rounded-lg bg-hearth-paper/90 text-hearth-mute hover:text-hearth-clay backdrop-blur cursor-pointer" data-testid={`plant-photo-${p.id}`}>
                    <Camera size={13} />
                    <input type="file" hidden accept="image/*" onChange={async (e) => {
                      const file = e.target.files?.[0]; e.target.value = "";
                      if (!file) return;
                      const fd = new FormData(); fd.append("file", file);
                      try { await api.post(`/plants/${p.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); load(); }
                      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }} />
                  </label>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display text-2xl leading-tight" style={{ color: "var(--ink)" }}>{p.nickname || p.name}</h3>
                  <p className="caption mt-0.5 italic" style={{ color: "var(--moss-soft)" }}>{c.scientific_name || p.name}</p>
                  {p.location && (
                    <p
                      className="mt-2"
                      style={{
                        fontFamily: '"Caveat", cursive',
                        color: "var(--moss)",
                        fontSize: "1rem",
                        opacity: 0.85,
                        transform: "rotate(-1deg)",
                        transformOrigin: "left",
                        display: "inline-block",
                      }}
                      data-testid={`plant-location-${p.id}`}
                    >
                      {locationPhrase(p.id, p.location)}
                    </p>
                  )}

                  {hasError ? (
                    <div className="mt-4 rounded-xl border border-hearth-coral/30 bg-hearth-coral/5 p-3">
                      <div className="flex items-start gap-2 text-hearth-coral">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">Care guide pending</p>
                          <p className="text-[11px] text-hearth-coral/80 mt-0.5">
                            {String(c.error).toLowerCase().includes("budget")
                              ? "AI budget was low when this was added — try again now."
                              : "Couldn't fetch the AI care guide."}
                          </p>
                        </div>
                      </div>
                      <button
                        data-testid={`plant-retry-${p.id}`}
                        onClick={(e) => { e.stopPropagation(); refresh(p.id); }}
                        disabled={refreshing === p.id}
                        className="mt-2 w-full pill-btn bg-hearth-coral text-white hover:bg-hearth-coral/90 text-xs inline-flex items-center justify-center gap-1.5"
                      >
                        <RefreshCw size={11} className={refreshing === p.id ? "animate-spin" : ""} />
                        {refreshing === p.id ? "Asking the gardener…" : "Try again"}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-4 space-y-2 text-sm">
                      {c.watering && <div className="flex gap-2"><Droplets size={15} className="shrink-0 mt-0.5" style={{ color: "var(--moss)" }} /><span style={{ color: "var(--slate-soft, #5D6572)" }}><span style={{ color: "var(--ink)" }}>Water:</span> {short(c.watering)}</span></div>}
                      {c.sunlight && <div className="flex gap-2"><Sun size={15} className="text-hearth-amber shrink-0 mt-0.5" /><span className="text-hearth-mute"><span style={{ color: "var(--ink)" }}>Light:</span> {short(c.sunlight)}</span></div>}
                      {c.temperature && <div className="flex gap-2"><Thermometer size={15} className="text-hearth-coral shrink-0 mt-0.5" /><span className="text-hearth-mute"><span style={{ color: "var(--ink)" }}>Temp:</span> {short(c.temperature)}</span></div>}
                    </div>
                  )}

                  <div className="mt-auto pt-4">
                    <div className="brass-hair" />
                    <div className="mt-3 flex items-center justify-between">
                      <span className="caption inline-flex items-center gap-1.5" style={{ color: "var(--botanica)" }}><Sparkles size={11} /> tap for the full guide</span>
                      <button data-testid={`plant-delete-${p.id}`} onClick={(e) => { e.stopPropagation(); remove(p.id); }} className="p-2 rounded-md text-hearth-mute hover:text-hearth-coral hover:bg-hearth-coral/10">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader><DialogTitle className="font-display text-3xl">New plant</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="caption text-hearth-mute">Plant name (species)</Label>
              <Input data-testid="plant-form-name" className="input-warm mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Monstera Deliciosa" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Nickname (optional)</Label>
              <Input data-testid="plant-form-nickname" className="input-warm mt-1.5 h-11" value={form.nickname || ""} onChange={(e) => setForm({ ...form, nickname: e.target.value })} placeholder="Monty" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Where it lives</Label>
              <Input data-testid="plant-form-location" className="input-warm mt-1.5 h-11" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Living room corner" />
            </div>
            <p className="text-xs text-hearth-mute leading-relaxed">
              We&apos;ll ask Claude (your AI gardener) for a tailored care guide — this can take a few seconds.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full" disabled={busy}>Cancel</Button>
            <button data-testid="plant-form-save" onClick={save} disabled={busy} className="btn-botanica inline-flex items-center text-sm">
              {busy ? "Consulting…" : "Add plant"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl">{selected.nickname || selected.name}</DialogTitle>
                <p className="text-hearth-mute italic">{selected.care?.scientific_name || selected.name}</p>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(selected.care || {}).map(([k, v]) => {
                  if (k === "error") return null;
                  return (
                    <div key={k} className="rounded-2xl border border-hearth-line bg-hearth-cream/50 p-4">
                      <p className="caption text-hearth-mute">{k.replace(/_/g, " ")}</p>
                      <p className="text-sm text-hearth-ink mt-1.5 whitespace-pre-wrap">{Array.isArray(v) ? v.join(", ") : String(v)}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function short(t) { return String(t).length > 80 ? String(t).slice(0, 78) + "…" : t; }
